import { useEffect, useMemo, useRef, useState } from 'react';
import { format } from 'date-fns';
import Modal from './Modal';
import AvailabilityTextSizeControls from './AvailabilityTextSizeControls';
import AvailabilityImageSettingsModal from './AvailabilityImageSettingsModal';
import AvailabilityDateRangePicker from './AvailabilityDateRangePicker';
import { AVAILABILITY_TEMPLATE_COMPONENTS } from './availabilityImage/templateRegistry';
import {
  AVAILABILITY_IMAGE_GROUPS,
  AVAILABILITY_IMAGE_TEMPLATES,
  AVAILABILITY_IMAGE_MULTI_DAY_GROUPS,
  AVAILABILITY_IMAGE_MULTI_DAY_TEMPLATES,
  AVAILABILITY_IMAGE_MULTI_DAY_LABELS,
  AVAILABILITY_IMAGE_RANGE_DEBOUNCE_MS,
} from '../constants/bookingConst';
import { spinnerRing } from '../utils/bookingUtils';
import { downloadDataUrl } from '../utils/imageExportUtil';
import { useImageBatchRenderer } from '../hooks/useImageBatchRenderer';
import { buildAvailabilityImageFileName, buildMultiDaySnapshot } from '../utils/availabilitySnapshotUtil';
import { loadImageSettings, saveImageSettings } from '../utils/availabilityImageSettingsUtil';
import { logAuditEvent, AUDIT_ACTION_TYPES, ENTITY_TYPES } from '../utils/auditLogUtil';
import '../css/AvailabilityImageModal.css';

/**
 * Templates bucketed by preview group, in the order the dialog shows them —
 * and generates them, so the phone sizes are ready first.
 * @param {Array<object>} groups - preview groups
 * @param {Array<object>} templates - the templates offered
 * @returns {Array<object>} groups that have templates, each with its `templates`
 */
const bucketTemplates = (groups, templates) => groups
  .map(group => ({ ...group, templates: templates.filter(t => t.group === group.id) }))
  .filter(group => group.templates.length > 0);

// One date: the single-day sizes. A range: the multi-day images, in the same
// phone sizes (story, feed post) plus their own wide size.
const SINGLE_DAY_GROUPS = bucketTemplates(AVAILABILITY_IMAGE_GROUPS, AVAILABILITY_IMAGE_TEMPLATES);
const MULTI_DAY_GROUPS = bucketTemplates(
  [...AVAILABILITY_IMAGE_GROUPS.filter(group => group.mobile), ...AVAILABILITY_IMAGE_MULTI_DAY_GROUPS],
  AVAILABILITY_IMAGE_MULTI_DAY_TEMPLATES,
);
const GROUP_SHORT_LABELS = Object.fromEntries(
  [...AVAILABILITY_IMAGE_GROUPS, ...AVAILABILITY_IMAGE_MULTI_DAY_GROUPS].map(group => [group.id, group.short]),
);

/**
 * Generates every availability image template for a date — or one combined
 * multi-day image for a date range — and lets the admin download whichever
 * they want.
 *
 * The templates are mounted off-screen at full export size and converted to
 * PNG in the background (useImageBatchRenderer), shown as previews as each
 * finishes. The dates, the gear and Regenerate stay usable throughout: any
 * change stops the load or generation in progress and starts over with the
 * new choice. The grid's own date is frozen by the caller when the button is
 * pressed (no reads); any other date or range is loaded once through
 * `loadRangeSnapshots` when the admin settles on it, then frozen the same
 * way, so a booking landing mid-generation can't make the images disagree.
 *
 * Built on the shared Modal: × close button, and a tap outside does nothing.
 *
 * @param {object} props
 * @param {boolean} props.isOpen
 * @param {() => void} props.onClose
 * @param {object|null} props.snapshot - buildAvailabilitySnapshot() output for the grid's selected date
 * @param {(startStr: string, endStr: string) => Promise<object[]>} props.loadRangeSnapshots - one
 *   buildAvailabilitySnapshot() output per date from start to end ('yyyy-MM-dd')
 * @returns {JSX.Element|null}
 */
function AvailabilityImageModal({ isOpen, onClose, snapshot, loadRangeSnapshots }) {
  const [downloadingId, setDownloadingId] = useState(null);
  const [downloadError, setDownloadError] = useState('');
  // Image settings (gear): font sizes in px and BOOKED colours, remembered
  // in this browser. Applying new ones regenerates the images.
  const [imageSettings, setImageSettings] = useState(loadImageSettings);
  const [showSettings, setShowSettings] = useState(false);
  // Bumped by Regenerate so it re-runs even when nothing else changed.
  const [generationRun, setGenerationRun] = useState(0);
  // Bumped by Regenerate on a loaded date/range, to read it again.
  const [reloadRun, setReloadRun] = useState(0);
  const templateRefs = useRef({});
  // The dates the images cover. Starts on the grid's own date each time the
  // dialog opens; a range turns on the multi-day images.
  const [range, setRange] = useState({ start: '', end: '' });
  // The snapshot for any range other than the grid's own date, once loaded.
  const [loadedSnapshot, setLoadedSnapshot] = useState(null);
  const [rangeLoading, setRangeLoading] = useState(false);
  const [rangeError, setRangeError] = useState('');

  // Held in a ref, not an effect dependency: the caller rebuilds it on every
  // render of the busy admin grid, and a new function must not refetch the
  // range the admin already picked.
  const loadRangeRef = useRef(loadRangeSnapshots);
  loadRangeRef.current = loadRangeSnapshots;

  const initialDate = snapshot?.dateStr || '';
  // Each opening starts on the grid's own date; closing clears the range so
  // the next opening never fetches the previous one's dates first.
  useEffect(() => {
    if (isOpen && initialDate) setRange({ start: initialDate, end: initialDate });
    else if (!isOpen) setRange({ start: '', end: '' });
  }, [isOpen, initialDate]);

  const isMultiDay = Boolean(range.start && range.end && range.start !== range.end);
  // The grid's own date needs no reads — it's the snapshot the caller froze.
  const usesInitialSnapshot = !isMultiDay && range.start === initialDate;

  // Loads any other date or range. Waits for the dates to settle first, and
  // a newer choice cancels an older load (its result is never shown).
  useEffect(() => {
    setLoadedSnapshot(null);
    setRangeError('');
    if (!isOpen || !range.start || usesInitialSnapshot) {
      setRangeLoading(false);
      return undefined;
    }
    let cancelled = false;
    setRangeLoading(true);
    const timer = setTimeout(async () => {
      try {
        const snapshots = await loadRangeRef.current(range.start, range.end);
        if (cancelled) return;
        setLoadedSnapshot(isMultiDay ? buildMultiDaySnapshot(snapshots) : (snapshots[0] || null));
      } catch (error) {
        if (!cancelled) setRangeError(error.message || 'Could not load those dates. Please try again.');
      } finally {
        if (!cancelled) setRangeLoading(false);
      }
    }, AVAILABILITY_IMAGE_RANGE_DEBOUNCE_MS);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [isOpen, range.start, range.end, usesInitialSnapshot, isMultiDay, reloadRun]);

  // What the images are drawn from right now.
  const activeSnapshot = usesInitialSnapshot ? snapshot : loadedSnapshot;
  const templateGroups = activeSnapshot?.isMultiDay ? MULTI_DAY_GROUPS : SINGLE_DAY_GROUPS;
  const orderedTemplates = templateGroups.flatMap(group => group.templates);

  const hasCourts = (activeSnapshot?.courts?.length || 0) > 0;
  // Passed hours are left off the images; late in the day there may be none left.
  const hasRows = (activeSnapshot?.rows?.length || 0) > 0;
  const canGenerate = Boolean(isOpen && activeSnapshot) && hasCourts && hasRows;

  // A new identity whenever the images must be redrawn: other data, other
  // settings, or a Regenerate press. The renderer restarts on it.
  const renderKey = useMemo(
    () => ({ activeSnapshot, imageSettings, generationRun }),
    [activeSnapshot, imageSettings, generationRun],
  );
  const { images, errors, generating } = useImageBatchRenderer({
    enabled: canGenerate,
    templates: orderedTemplates,
    nodesRef: templateRefs,
    runKey: renderKey,
  });

  // A failed download belongs to the images it was about; new ones clear it.
  useEffect(() => { setDownloadError(''); }, [renderKey]);

  if (!isOpen || !snapshot) return null;

  /**
   * Saves one generated image and records it on the audit trail.
   * @param {{id: string, label: string, group: string}} template
   * @returns {Promise<void>}
   */
  const handleDownload = async (template) => {
    setDownloadError('');
    setDownloadingId(template.id);
    try {
      await downloadDataUrl(images[template.id], buildAvailabilityImageFileName(activeSnapshot.dateStr, template.id));
      logAuditEvent({
        actionType: AUDIT_ACTION_TYPES.AVAILABILITY_IMAGE_DOWNLOADED,
        entityType: ENTITY_TYPES.SLOT,
        // No record of its own — the date (or "start-to-end" range) it shows
        // stands in as the id, the same way a slot edit uses its path.
        entityId: `${activeSnapshot.dateStr}/availability-image`,
        summary: {
          type: 'availabilityImage',
          date: activeSnapshot.isMultiDay
            ? `${activeSnapshot.startDateStr} \u2013 ${activeSnapshot.endDateStr}`
            : activeSnapshot.dateStr,
          templateId: template.id,
          // The same design comes in several sizes, so the size is named too.
          templateLabel: `${template.label} - ${GROUP_SHORT_LABELS[template.group] || ''}`,
          sportName: activeSnapshot.sportName || null,
          openSlots: activeSnapshot.openCount,
          totalSlots: activeSnapshot.totalCount,
        },
      });
    } catch (error) {
      setDownloadError(error.message || 'The image could not be downloaded. Please try again.');
    } finally {
      setDownloadingId(null);
    }
  };

  /**
   * Starts over: reads a loaded date/range again (fresh bookings), or
   * redraws the grid's own date from its frozen snapshot. Either way the run
   * in progress is dropped.
   * @returns {void}
   */
  const handleRegenerate = () => {
    if (usesInitialSnapshot) setGenerationRun(run => run + 1);
    else setReloadRun(run => run + 1);
  };

  /**
   * Saves the settings from the gear dialog and redraws with them (the
   * renderer restarts on imageSettings).
   * @param {object} next - normalized settings
   * @returns {void}
   */
  const handleApplySettings = (next) => {
    saveImageSettings(next);
    setImageSettings(next);
    setShowSettings(false);
  };

  const readyCount = Object.keys(images).length;
  // Past dates have nothing left to post, so the picker starts at today —
  // or at the grid's own date when the admin is viewing an earlier one.
  const todayStr = format(new Date(), 'yyyy-MM-dd');
  const minDate = initialDate && initialDate < todayStr ? initialDate : todayStr;

  return (
    <>
      <Modal
        isOpen={isOpen}
        onClose={onClose}
        title="Availability images"
        hideIcon
        confirmText="Close"
        className="aim-modal"
      >
        <div className="aim-toolbar">
          <AvailabilityDateRangePicker value={range} onChange={setRange} minDate={minDate} />
          {/* Always available — even while loading or generating, where a
              change simply restarts the work with the new choice. */}
          <AvailabilityTextSizeControls
            onOpenSettings={() => setShowSettings(true)}
            onRegenerate={handleRegenerate}
            generating={generating || rangeLoading}
          />
        </div>

        {activeSnapshot && !rangeLoading && (
          <p className="aim-subtitle">
            {activeSnapshot.dateLong}
            {activeSnapshot.sportName ? ` · ${activeSnapshot.sportName}` : ''}
            {' · '}
            {activeSnapshot.openCount} of {activeSnapshot.totalCount} slots open
          </p>
        )}

        {rangeLoading && (
          <div className="aim-range-loading">{spinnerRing(AVAILABILITY_IMAGE_MULTI_DAY_LABELS.LOADING)}</div>
        )}
        {rangeError && <p className="aim-error">{rangeError}</p>}

        {activeSnapshot && !hasCourts && (
          <p className="aim-empty">There are no bookable courts on this view to put on an image.</p>
        )}

        {activeSnapshot && hasCourts && !hasRows && (
          <p className="aim-empty">
            {activeSnapshot.isMultiDay
              ? 'Every hour in these dates has already passed, so there is nothing left to post.'
              : 'Every hour on this date has already passed, so there is nothing left to post.'}
          </p>
        )}

        {canGenerate && (
          <>
            <p className="aim-status">
              {generating
                ? `Generating images in the background… ${readyCount} of ${orderedTemplates.length} ready. You can change the dates or settings at any time.`
                : 'Pick the image you want and download it.'}
            </p>
            {downloadError && <p className="aim-error">{downloadError}</p>}

            {templateGroups.map(group => (
              <section key={group.id} className="aim-group">
                <h3 className="aim-group-title">{group.label}</h3>
                <div className={`aim-grid${group.mobile ? ' aim-grid-mobile' : ''}`}>
                  {group.templates.map((template) => {
                    const imageUrl = images[template.id];
                    const error = errors[template.id];
                    // "Full grid (dark) · Time ranges" -> name on one line, row mode under it.
                    const [labelName, labelMode] = template.label.split(' \u00b7 ');
                    return (
                      <div key={template.id} className="aim-card">
                        <div className="aim-preview" style={{ aspectRatio: `${template.width} / ${template.height}` }}>
                          {imageUrl && <img src={imageUrl} alt={`${template.label} preview`} />}
                          {!imageUrl && !error && <div className="aim-preview-loading">{spinnerRing('Generating…')}</div>}
                          {error && <div className="aim-preview-error">Couldn&apos;t generate this image. {error}</div>}
                        </div>
                        <div className="aim-card-foot">
                          <span className="aim-card-label">
                            <span className="aim-card-name">{labelName}</span>
                            {labelMode && <span className="aim-card-mode">{labelMode}</span>}
                          </span>
                          <button
                            type="button"
                            className="aim-download-btn"
                            disabled={!imageUrl || downloadingId === template.id}
                            onClick={() => handleDownload(template)}
                          >
                            {downloadingId === template.id ? 'Saving…' : 'Download'}
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </section>
            ))}

            {/* Full-size templates, off-screen. They must be laid out (not
                display:none) for html-to-image to measure and clone them. */}
            <div className="aim-offscreen" aria-hidden="true">
              {orderedTemplates.map((template) => {
                const Template = AVAILABILITY_TEMPLATE_COMPONENTS[template.id];
                if (!Template) return null;
                return (
                  <Template
                    key={template.id}
                    ref={(node) => { templateRefs.current[template.id] = node; }}
                    snapshot={activeSnapshot}
                    format={template.format}
                    rowMode={template.rowMode}
                    imageSettings={imageSettings}
                  />
                );
              })}
            </div>
          </>
        )}
      </Modal>

      {/* A sibling of the images dialog, not a child, so its fixed overlay
          isn't positioned inside the dialog's animated container. */}
      <AvailabilityImageSettingsModal
        isOpen={showSettings}
        settings={imageSettings}
        onApply={handleApplySettings}
        onClose={() => setShowSettings(false)}
      />
    </>
  );
}

export default AvailabilityImageModal;
